import React, { Fragment, useEffect, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify';
import { populatePdfForm, fetchFlattenedPdfTemplate, getBulkFileName } from '../utils';
import { fetchWrapper, getFormDataWithValue } from '@/_helpers';
import { MdOutlineFileDownload } from 'react-icons/md';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import LoadingSpiner from '@/_elements/LoadingSpiner';
import { css } from 'twin.macro';

const DOWNLOAD_LIMIT = 250;
let pdfDimensionMap = {};
let pdfBytesMap = {};
let imageBytesMap = {};

const generateUrl = ({
    sortBy = { key: 'createdAt', direction: 'desc' },
    currentPage = 1,
    teamId,
    formName,
    member,
    channel,
    startDate,
    endDate,
    searchTags = [],
    submissionType,
    channelIdFromUrl,
    limit,
}) => {
    let url = `/userFormData?sortBy=${sortBy.key}:${sortBy.direction}&limit=${limit || 10}&page=${currentPage}&hasPdfTemplate=true`;
    if (formName) {
        if (Array.isArray(formName) && formName.length) {
            url += `&forms=${formName.map((form) => form.value).join(',')}`;
        } else if (formName.value) {
            url += `&form=${formName.value}`;
        }
    }
    if (member?.value) url += `&user=${member.value}`;
    if (channelIdFromUrl || channel?.value) {
        url += `&chatroom=${channelIdFromUrl || channel?.value}`;
    } else if (teamId) {
        url += `&team=${teamId}`;
    }
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    if (searchTags?.length) url += `&tags=${searchTags.join(',')}`;
    if (submissionType?.value) {
        if (submissionType.value === 'urlSubmission') {
            url += `&type=normal&urlSubmission=true`;
        } else {
            url += `&submissionType=${submissionType.value}&type=normal`;
        }
    }

    return url;
};
const fetchPdfTemplateForms = async ({ filters, setLoading = () => {}, setTotalResults = () => {} }) => {
    try {
        setLoading(true);
        if (!filters.teamId) {
            throw new Error('Please select a team');
        }
        const url = generateUrl({
            ...filters,
            limit: DOWNLOAD_LIMIT + 1,
        });
        const response = (await fetchWrapper.get(url)).results;
        const forms = response
            .filter(({ formData }) => formData && formData?.pdfTemplate && formData?.pdfTemplate?.url)
            .map((form) => {
                return {
                    pdfTemplateUrl: form.formData.pdfTemplate.url,
                    formTemplateUrl: form.formData.pdfTemplate.url,
                    formElements: getFormDataWithValue(form.formData.data, form.data),
                };
            });
        setTotalResults(forms.length);
        return forms;
    } catch (error) {
        if (error?.name !== 'AbortError') {
            toast.error(error.message);
        }
        return [];
    } finally {
        setLoading(false);
    }
};

const BulkTemplatePdfDownload = ({ disabled, filters = {} }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [show, setShow] = useState(false);
    const handleResetState = () => {
        pdfDimensionMap = {};
        pdfBytesMap = {};
        imageBytesMap = {};
        setIsDownloading(false);
        setFilesDownloaded(0);
        setTotalFilesToDownload(0);
    };
    const handleShow = () => {
        handleResetState();
        setShow(true);
    };
    const handleClose = () => setShow(false);
    const [loading, setLoading] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [filesDownloaded, setFilesDownloaded] = useState(0);
    const [totalFilesToDownload, setTotalFilesToDownload] = useState(0);

    const handleBulkDownload = async () => {
        handleResetState();
        setIsDownloading(true);
        try {
            const formsToDownload = await fetchPdfTemplateForms({ filters, setLoading, setTotalResults });
            const totalFiles = formsToDownload.length;
            setTotalFilesToDownload(totalFiles > DOWNLOAD_LIMIT ? DOWNLOAD_LIMIT : totalFiles);

            const mergedPdf = await PDFDocument.create();

            for (const formData of formsToDownload) {
                const { formTemplateUrl, formElements = [] } = formData;

                const templateFormElements = (formElements || [])?.filter(
                    (x) => x?.config?.templateConfig || x?.config?.hasTemplateConfig
                );
                let pdfBytes = '';

                if (pdfBytesMap[formTemplateUrl]) {
                    pdfBytes = pdfBytesMap[formTemplateUrl];
                } else {
                    pdfBytes = await fetchFlattenedPdfTemplate(formTemplateUrl);
                    pdfBytesMap[formTemplateUrl] = pdfBytes;
                    const pdfDoc = await PDFDocument.load(pdfBytes);
                    const pages = pdfDoc.getPages();

                    const localPdfDimensionMap = pdfDimensionMap[formTemplateUrl] || {};
                    pages.forEach((page, index) => {
                        const { width, height } = page.getSize();
                        localPdfDimensionMap[index + 1] = { width, height };
                    });
                    pdfDimensionMap[formTemplateUrl] = localPdfDimensionMap;
                }

                const populatedPdfBytes = await populatePdfForm({
                    pdfBytes,
                    templateFormElements,
                    pageDimensionMap: pdfDimensionMap[formTemplateUrl],
                    pdfDimension: undefined,
                    imageBytesMap,
                });

                const populatedPdfDoc = await PDFDocument.load(populatedPdfBytes);
                const copiedPages = await mergedPdf.copyPages(populatedPdfDoc, populatedPdfDoc.getPageIndices());
                copiedPages.forEach((page) => mergedPdf.addPage(page));
                setFilesDownloaded((prev) => prev + 1);
            }

            const mergedPdfBytes = await mergedPdf.save();
            const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = getBulkFileName();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success('Downloaded successfully!');
        } catch (error) {
            console.error('Error during bulk PDF download:', error);
            toast.error(`Failed to download and merge PDFs: ${error.message}`);
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        if (show && filters?.teamId) {
            fetchPdfTemplateForms({ filters, setLoading, setTotalResults });
        }
    }, [show]);
    return (
        <Fragment>
            <button className="btn btn-primary" onClick={handleShow} disabled={disabled}>
                <MdOutlineFileDownload className="mr-1" />
                Bulk Pdf Template
            </button>
            <Modal show={show} centered backdrop="static" size="xl">
                <Modal.Header>
                    <Modal.Title id="contained-modal-title-vcenter">Bulk Pdf Template Form Download</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        {loading ? (
                            <div className="d-flex justify-content-center">
                                <LoadingSpiner size={40} />
                            </div>
                        ) : (
                            <div>
                                <p>
                                    Total Results Found:{' '}
                                    {totalResults && totalResults > DOWNLOAD_LIMIT
                                        ? `>=${DOWNLOAD_LIMIT}`
                                        : totalResults}
                                </p>
                                <p>Download Limit: {DOWNLOAD_LIMIT}</p>
                                <div className="d-flex justify-content-center">
                                    {isDownloading && (
                                        <div style={{ marginTop: '100px', marginBottom: '100px', width: '80%' }}>
                                            <LoadingSpiner size={40} />
                                            <ProgressBar
                                                animated
                                                min={0}
                                                now={Number(
                                                    `${((filesDownloaded || 0) / (totalFilesToDownload || 1)) * 100}`
                                                )}
                                                label={`${Math.round(((filesDownloaded || 0) / (totalFilesToDownload || 1)) * 100)} %`}
                                                css={css`
                                                    height: 30px;
                                                    font-size: 24px;
                                                    width: 100%;
                                                    margin-top: 30px;
                                                `}
                                            />
                                            <div className="text-center">{`( ${filesDownloaded} / ${totalFilesToDownload} ) forms processed ...`}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                        <Fragment>
                            {isDownloading ? null : (
                                <Button disabled={isDownloading || loading} onClick={() => handleBulkDownload()}>
                                    <span className="mr-2">Start Download</span>
                                </Button>
                            )}
                        </Fragment>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isDownloading || loading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default BulkTemplatePdfDownload;
